import {AnimatePresence, motion} from 'framer-motion';
import {Button, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import type {ReactElement, RefObject} from 'react';
import {createPortal} from 'react-dom';

import {buildCategoryItems} from '../../hooks/useFilterCategorySelection';
import {useIsMobileView} from '../../hooks/useIsMobileView';
import {usePresetState} from '../../hooks/usePresetState';
import {useColumnStates} from '../../store/selectors';
import type {FeGridSettings, PresetDisplayMode} from '../../types/Api';
import type {EagColumn} from '../../types/EagColumn';
import type {EagColumnOrGroup} from '../../types/EagColumnGroup';
import type {Filters} from '../../types/Filters';
import type {FormToDataGridConnectorProps} from '../../types/FormToDataGridConnectorProps';
import {ToolpanelType} from '../../types/ToolpanelType';
import {getPresetStates} from '../../utils/getPresetStates';
import {MobileSheet} from '../MobileSheet/MobileSheet';
import {ColumnsPanel} from './components/ColumnsPanel/ColumnsPanel';
import {CustomFiltersPanel} from './components/CustomFiltersPanel/CustomFiltersPanel';
import {FilterPanel} from './components/FilterPanel/FilterPanel';
import {MobilePresetsDialog} from './components/MobilePresetsDialog/MobilePresetsDialog';
import {SettingsPanel} from './components/SettingsPanel/SettingsPanel';
import {
  SettingsGearIcon,
  SidebarPanelHeader,
} from './components/SidebarPanelHeader/SidebarPanelHeader';

export type SidebarProps = {
  columns: EagColumn[];
  columnConfig: EagColumnOrGroup[];
  filterState: Filters;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
  settings: FeGridSettings;
  columnSortMode?: 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE';
  onSettingsChange: (settings: Partial<FeGridSettings>) => void;
  /** Current preset display mode — forwarded to SettingsPanel. */
  presetDisplayMode?: PresetDisplayMode;
  /** Callback to change the preset display mode. */
  onPresetDisplayModeChange?: (mode: PresetDisplayMode) => void;
  filterComponent?: (props: FormToDataGridConnectorProps) => ReactElement;
  activePanel: ToolpanelType | null;
  onClose: () => void;
  isCompact: boolean;
  /** Controls whether the summary row toggle is enabled in SettingsPanel. */
  summaryRowEnabled?: boolean;
  /** The grid's table element — forwarded to ColumnsPanel for reveal-in-grid. */
  tableRef?: RefObject<HTMLTableElement | null>;
  /** Feature-flag gate for the ColumnsPanel scroll-to features. */
  columnsPanelScrollToEnabled?: boolean;
};

const SLIDE_PANEL_DEFAULT_WIDTH = 320;
const SLIDE_PANEL_FILTER_WIDTH = 'clamp(320px, 30vw, 380px)';
const SLIDE_PANEL_COLUMN_WIDTH = '360px';
const SLIDE_PANEL_SETTINGS_WIDTH = '360px';

// Per-panel slide-panel width and header-title translation key, keyed by panel
// type so adding a panel means one entry here instead of editing two parallel
// `match` statements. Width is overridden to full-width on mobile; the title key
// is resolved through the translation context at render.
const PANEL_META: Record<ToolpanelType, {width: number | string; titleKey: string}> = {
  [ToolpanelType.filter]: {
    width: SLIDE_PANEL_FILTER_WIDTH,
    titleKey: 'page.datagrid.labels.filters',
  },
  [ToolpanelType.column]: {
    width: SLIDE_PANEL_COLUMN_WIDTH,
    titleKey: 'page.datagrid.labels.columns',
  },
  [ToolpanelType.settings]: {
    width: SLIDE_PANEL_SETTINGS_WIDTH,
    titleKey: 'page.datagrid.labels.settings',
  },
  [ToolpanelType.presets]: {
    width: SLIDE_PANEL_DEFAULT_WIDTH,
    titleKey: 'page.datagrid.labels.presets',
  },
};

const DOCK_ANIMATION = {
  initial: {opacity: 0},
  animate: {opacity: 1},
  exit: {opacity: 0},
  transition: {duration: 0.15, ease: 'easeInOut'} as const,
};

// z-fullscreen-modal-component keeps the drawer above dialogs (z-modal) AND
// FullScreenModals (z-fullscreen-modal) when the grid is rendered inside one
// (e.g. AddToSupplierOrderModal) — the drawer is portaled to document.body.
const COMPACT_DRAWER_CLASS =
  'fixed top-12 right-0 bottom-0 bg-white flex flex-col overflow-hidden z-(--z-fullscreen-modal-component)';

const PANEL_BORDER_STYLE: React.CSSProperties = {
  borderLeft: '1px solid #EBECF0',
  boxShadow: 'rgba(9, 30, 66, 0.12) -8px 0px 24px',
};

export function Sidebar(props: SidebarProps) {
  const t = useTranslationContext();
  const isMobile = useIsMobileView();
  const presetState = usePresetState();
  const columnStates = useColumnStates();

  const currentColumnSettings = getPresetStates(columnStates);

  const handleCreatePreset = (name: string, desc: string) =>
    presetState.createPreset(name, desc, currentColumnSettings, props.settings);

  const handleUpdatePreset = (presetId: string) =>
    presetState.updatePreset(presetId, currentColumnSettings, props.settings);

  const panelMeta = props.activePanel != null ? PANEL_META[props.activePanel] : null;

  // Mobile never reaches the slide panel or compact drawer (both gated on !isMobile below) —
  // only the MobileSheet, which sizes itself — so this width only matters for desktop/compact.
  const sidebarWidth = panelMeta?.width ?? SLIDE_PANEL_DEFAULT_WIDTH;

  const panelContent = match(props.activePanel)
    .with(ToolpanelType.column, () => (
      <ColumnsPanel
        columns={props.columns}
        columnConfig={props.columnConfig}
        tableRef={props.tableRef}
        scrollToEnabled={props.columnsPanelScrollToEnabled}
      />
    ))
    .with(ToolpanelType.filter, () => {
      if (props.filterComponent) {
        return <CustomFiltersPanel filterComponent={props.filterComponent} />;
      }
      return (
        <FilterPanel
          columnConfig={props.columnConfig}
          filterState={props.filterState}
          onFilterChange={props.onFilterChange}
        />
      );
    })
    .with(ToolpanelType.settings, () => (
      <SettingsPanel
        settings={props.settings}
        columnSortMode={props.columnSortMode}
        onSettingsChange={props.onSettingsChange}
        presetDisplayMode={props.presetDisplayMode}
        onPresetDisplayModeChange={props.onPresetDisplayModeChange}
        summaryRowEnabled={props.summaryRowEnabled}
      />
    ))
    .with(ToolpanelType.presets, () => (
      <MobilePresetsDialog
        presets={presetState.presets}
        activePreset={presetState.activePreset}
        isPresetChanged={presetState.isPresetChanged}
        onActivate={presetState.activatePreset}
        onDeactivate={presetState.deactivatePreset}
        onCreate={handleCreatePreset}
        onUpdate={handleUpdatePreset}
        onDelete={presetState.deletePreset}
        onDuplicate={presetState.duplicatePreset}
        onApplyToDefault={presetState.applyPresetToVirtual}
        onRename={presetState.renamePreset}
        onResetOverrides={() => presetState.setIsPresetChanged(false)}
        onClose={props.onClose}
      />
    ))
    .with(null, () => null)
    .exhaustive();

  const panelTitle = panelMeta ? t(panelMeta.titleKey) : '';

  // Number of currently-visible columns — shown as the badge next to the "Columns" title.
  const columnVisibleCount = columnStates.filter((cs) => !(cs.hidden ?? false)).length;

  // Total active filter count for the Filters header badge (built-in filter panel only).
  const filterActiveCount =
    props.activePanel === ToolpanelType.filter && !props.filterComponent
      ? buildCategoryItems(props.columnConfig, props.filterState).reduce(
          (sum, cat) => sum + cat.activeCount,
          0
        )
      : 0;

  // Header badge: visible-column count for the columns panel, active-filter count for filters.
  const headerBadge =
    props.activePanel === ToolpanelType.column
      ? {count: columnVisibleCount, testId: 'columns-count-badge'}
      : {count: filterActiveCount, testId: 'filter-active-count-badge'};

  const panelInner = (
    <>
      <SidebarPanelHeader
        title={panelTitle}
        leadingIcon={
          props.activePanel === ToolpanelType.settings ? <SettingsGearIcon /> : undefined
        }
        badge={headerBadge}
        onClose={props.onClose}
      />
      <div className="flex-1 overflow-hidden">{panelContent}</div>
    </>
  );

  // On mobile every panel renders inside a MobileSheet instead of the desktop
  // slide panel / compact drawer.
  const showMobileSheet = isMobile && props.activePanel != null;
  const showSlidePanel = props.activePanel != null && !isMobile && !props.isCompact;
  const showCompactDrawer = props.isCompact && props.activePanel != null && !isMobile;

  return (
    <>
      <AnimatePresence mode="wait">
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {showSlidePanel && (
          <motion.div
            key={props.activePanel}
            data-testid="eagGrid-sidebarPanel"
            className="absolute inset-y-0 right-0 bg-white flex flex-col overflow-hidden"
            style={{
              width: sidebarWidth,
              zIndex: 30,
              ...PANEL_BORDER_STYLE,
            }}
            initial={DOCK_ANIMATION.initial}
            animate={DOCK_ANIMATION.animate}
            exit={DOCK_ANIMATION.exit}
            transition={DOCK_ANIMATION.transition}
            onClick={(e) => e.stopPropagation()}
          >
            {panelInner}
          </motion.div>
        )}
      </AnimatePresence>

      {showCompactDrawer &&
        createPortal(
          <div
            data-testid="eagGrid-sidebarPanel"
            className={COMPACT_DRAWER_CLASS}
            style={{
              width: sidebarWidth,
              ...PANEL_BORDER_STYLE,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {panelInner}
          </div>,
          document.body
        )}

      <Show when={showMobileSheet}>
        <MobileSheet
          isOpen
          onClose={props.onClose}
          title={panelTitle}
          badgeCount={headerBadge.count}
          footer={
            // Grid-level reset only for the built-in filter panel: onResetFilters clears the
            // grid's own filter state, which a custom filterComponent form doesn't reflect.
            props.activePanel === ToolpanelType.filter && !props.filterComponent ? (
              <Button
                variant="secondary"
                isFullWidth
                onClick={props.onResetFilters}
                title={t('page.datagrid.button.resetFilters')}
              />
            ) : undefined
          }
          data-testid="eagGrid-sidebar"
        >
          <div className="flex flex-col h-full overflow-hidden">{panelContent}</div>
        </MobileSheet>
      </Show>
    </>
  );
}
